'use client';

import { useState } from 'react';
import { Check, Loader2, ShoppingCart } from 'lucide-react';
import { Icon } from '@/components/ui/Icon';
import { BubbleRows } from '@/components/chat/BubbleBlock';
import { saveFromChat } from '@/lib/chat/block-actions';
import { ORDER_STAGE_EVENT, type OrderStageDetail } from '@/lib/chat/order-ended';
import type { SupplierOrderBlock } from '@/lib/chat/blocks/supplier-order';
import {
  partsChecklistItems,
  partsEmptyLabel,
  partsFootnote,
  partsLeftLabel,
  partsOrderLabel,
  partsOrderRequest,
  partsQuantityLabel,
  partsToOrder,
  type PartsChecklistBlock,
  type PartsChecklistItem,
  type PartsChecklistJob,
  type PartsPickedUp,
} from '@/lib/chat/blocks/parts-checklist';

/**
 * What to pick up this week, one "Got it" button per part (`parts-checklist.ts`).
 *
 * It was a bare tick box beside an Order button, and on 4 Nov 2026 Tom ticked
 * a part thinking it put it in the order: it marked it picked up, so the
 * Order button left with it and nothing was ordered. A button that says what
 * it does cannot be read as choosing what to buy, and the Order button sits
 * at the top of the job and names what it orders.
 *
 * "Got it" SAVES on the tap (`saveFromChat` → `/api/jobs/[id]/part-pickups`), so
 * the lad at the merchant and the office are looking at the same list. It
 * reaches nobody, which is why it is a direct write and not an approval.
 *
 * The button moves at once; if the server refuses, it goes back and the server's
 * own sentence sits under that line (#35, #42 — inline, never a toast). The
 * "left to pick up" count is the only number here and it is a count of the
 * boxes on screen. There are no prices on this block to show (#23).
 *
 * On an older reply (`live` false) the buttons are as they stood then and do
 * not tap: somebody may have ticked since, and a stale tick toggled from a
 * scrolled-back reply would untick their work.
 *
 * For somebody who may order parts (`block.order`), each job offers its lines
 * still to get as one order. The button only PREPARES it
 * (`/api/supplier-orders/from-parts`): the basket card arrives as Mucka's next
 * message, and nothing is bought until it is approved there with a passkey.
 */
type OrderTap =
  | { step: 'busy' }
  | { step: 'branch'; branches: Array<{ id: string; name: string }> }
  | { step: 'sent' }
  | { step: 'error'; message: string };

export function PartsChecklist({ block, live, onOpen }: {
  block: PartsChecklistBlock;
  live: boolean;
  onAnswer: (text: string) => void;
  onOpen: (path: string) => void;
}) {
  const key = (jobId: string, lineId: string) => `${jobId}:${lineId}`;
  const [picked, setPicked] = useState<Record<string, PartsPickedUp | null>>(() => {
    const init: Record<string, PartsPickedUp | null> = {};
    for (const job of block.jobs) for (const item of job.items) init[key(job.jobId, item.lineId)] = item.picked;
    return init;
  });
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [orders, setOrders] = useState<Record<string, OrderTap>>({});

  const order = async (job: PartsChecklistJob, branchId?: string) => {
    if (!live || orders[job.jobId]?.step === 'busy') return;
    // What is on screen now, ticks included: a line ticked since the reply is not ordered.
    const now = { ...job, items: job.items.map(i => ({ ...i, picked: picked[key(job.jobId, i.lineId)] ?? null })) };
    setOrders(prev => ({ ...prev, [job.jobId]: { step: 'busy' } }));
    const req = partsOrderRequest(now, branchId);
    const out = await saveFromChat('POST', req.path, req.body);
    const data = (out.data ?? {}) as { block?: SupplierOrderBlock; text?: string; branches?: Array<{ id: string; name: string }> };
    if (!out.ok) {
      setOrders(prev => ({ ...prev, [job.jobId]: { step: 'error', message: out.message ?? '' } }));
    } else if (data.branches?.length) {
      setOrders(prev => ({ ...prev, [job.jobId]: { step: 'branch', branches: data.branches! } }));
    } else if (data.block) {
      // The card goes in the chat as Mucka's next message, the way each step
      // of an order does (order-ended.ts).
      window.dispatchEvent(new CustomEvent<OrderStageDetail>(ORDER_STAGE_EVENT, {
        detail: { orderId: data.block.orderId, stage: 'prepared', text: data.text ?? '', block: data.block },
      }));
      setOrders(prev => ({ ...prev, [job.jobId]: { step: 'sent' } }));
    } else {
      setOrders(prev => ({ ...prev, [job.jobId]: { step: 'error', message: "That didn't work, so nothing has been ordered. Try again." } }));
    }
  };

  const total = partsChecklistItems(block).length;
  const left = Object.values(picked).filter(p => !p).length;
  const empty = partsEmptyLabel(block);
  const footnote = partsFootnote(block);

  const toggle = async (jobId: string, item: PartsChecklistItem) => {
    const { lineId } = item;
    const source = item.source ?? 'quote';
    const k = key(jobId, lineId);
    if (!live || busy[k]) return;
    const was = picked[k] ?? null;
    setPicked(prev => ({ ...prev, [k]: was ? null : { by: 'You', at: new Date().toISOString() } }));
    setBusy(prev => ({ ...prev, [k]: true }));
    setErrors(prev => ({ ...prev, [k]: null }));
    // A part written down on the job ticks its own row (collected / back to
    // in); a quote line is added to the job's parts as collected. One table.
    const body: Record<string, string> = source === 'part' ? { partId: lineId } : { quoteLineItemId: lineId };
    // Undone, it goes back to what this reply drew it as: still to get, so
    // the Order button offers it again, or on order. A line drawn already
    // picked up never showed what it was, so the server keeps its default.
    if (was && !item.picked) body.back = item.ordered ? 'ready' : 'needed';
    const out = await saveFromChat(was ? 'DELETE' : 'POST', `/api/jobs/${encodeURIComponent(jobId)}/part-pickups`, body);
    if (!out.ok) {
      setPicked(prev => ({ ...prev, [k]: was }));
      setErrors(prev => ({ ...prev, [k]: out.message }));
    }
    setBusy(prev => ({ ...prev, [k]: false }));
  };

  return (
    <div className="mchat-parts" role="group" aria-label={`Parts to pick up, ${block.rangeLabel}`}>
      <div className="mchat-parts-head">
        <span className="mchat-parts-left" aria-live="polite">
          {total ? partsLeftLabel(left) : 'Nothing to pick up'}
        </span>
        <span className="mchat-parts-range">{block.rangeLabel}</span>
      </div>

      {empty && <p className="mchat-parts-empty">{empty}</p>}

      {block.jobs.map(job => (
        <div className="mchat-parts-job" key={job.jobId}>
          <div className="mchat-group">{job.label}</div>
          {block.order && live && <OrderRow job={job} merchant={block.order.merchant} tap={orders[job.jobId]} picked={picked} onOrder={order} />}
          {job.items.map(item => {
            const k = key(job.jobId, item.lineId);
            const p = picked[k] ?? null;
            const qty = partsQuantityLabel(item.quantity);
            const by = p ? (p.by === 'You' ? 'Picked up by you' : p.by ? `Picked up by ${p.by}` : 'Picked up') : item.ordered ? 'On order' : null;
            return (
              <div className="mchat-parts-line" key={k}>
                <div className={`mchat-parts-item${p ? ' mchat-parts-item--on' : ''}`}>
                  <span className="mchat-parts-main">
                    <span className="mchat-parts-name">{item.name}</span>
                    {by && <span className="mchat-parts-by">{by}</span>}
                  </span>
                  {qty && <span className="mchat-parts-qty">{qty}</span>}
                  <button
                    type="button"
                    aria-pressed={!!p}
                    aria-label={p ? `Got ${item.name}. Tap to undo` : `Got ${item.name}`}
                    aria-busy={busy[k] || undefined}
                    className={`mchat-parts-got${p ? ' mchat-parts-got--on' : ''}`}
                    disabled={!live || !!busy[k]}
                    onClick={() => { void toggle(job.jobId, item); }}
                  >
                    {p && <Icon icon={Check} size={14} />}
                    Got it
                  </button>
                </div>
                {errors[k] && <p className="mchat-body mchat-error" role="alert">{errors[k]}</p>}
              </div>
            );
          })}
        </div>
      ))}

      {footnote && <p className="mchat-parts-foot">{footnote}</p>}
      {total > 0 && (
        <p className="mchat-parts-saved">
          {live && <Icon icon={Check} size={13} className="mchat-parts-saved-icon" />}
          {live
            ? "Tap Got it when you've picked a part up. The team sees it straight away."
            : 'As it stood when you asked. Ask again for the latest.'}
        </p>
      )}

      <BubbleRows rows={[{ id: 'open-week', title: block.openLabel, onPress: () => onOpen(block.openPath) }]} />
    </div>
  );
}

function OrderRow({ job, merchant, tap, picked, onOrder }: {
  job: PartsChecklistJob;
  merchant: string;
  tap: OrderTap | undefined;
  picked: Record<string, PartsPickedUp | null>;
  onOrder: (job: PartsChecklistJob, branchId?: string) => void;
}) {
  const toGet = partsToOrder({ items: job.items.filter(i => !picked[`${job.jobId}:${i.lineId}`]) });
  if (!toGet.length && tap?.step !== 'sent') return null;
  if (tap?.step === 'sent') {
    return <p className="mchat-parts-order-note" role="status">The order is ready to check and approve below. Nothing is bought until you approve it.</p>;
  }
  if (tap?.step === 'branch') {
    return (
      <div className="mchat-parts-order" role="group" aria-label={`Which ${merchant} branch?`}>
        <p className="mchat-parts-order-note">Which branch are you collecting from?</p>
        {tap.branches.map(b => (
          <button key={b.id} type="button" className="mchat-parts-order-btn mchat-parts-order-btn--choice" onClick={() => onOrder(job, b.id)}>{b.name}</button>
        ))}
      </div>
    );
  }
  const busy = tap?.step === 'busy';
  return (
    <div className="mchat-parts-order">
      <button type="button" className="mchat-parts-order-btn" disabled={busy} aria-busy={busy || undefined} onClick={() => onOrder(job)}>
        {busy
          ? <Icon icon={Loader2} size={16} className="mchat-parts-order-spin" aria-hidden />
          : <Icon icon={ShoppingCart} size={16} />}
        {busy ? `Finding these on ${merchant}…` : partsOrderLabel(toGet, merchant)}
      </button>
      {tap?.step === 'error' && <p className="mchat-body mchat-error" role="alert">{tap.message}</p>}
    </div>
  );
}
